import React, {
	ClipboardEvent,
	ClipboardEventHandler,
	KeyboardEventHandler,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import isHotkey from 'is-hotkey';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { classNames } from 'primereact/utils';
import {
	RenderLeafProps,
	RenderElementProps,
	EditorValue,
	SlateRenderElementProps,
	CustomEditor,
	MarkFormat,
} from 'components/_new/NewSlateEditor/types';
import { getInputBaseClassName } from 'components/_new/InputBase';
import { getMarked, getBlock, toggleMark } from './utils/SlateUtilityFunctions';
import { withLinks } from './plugins/withLinks';
import { withEmbeds } from './plugins/withEmbeds';
import Toolbar from './Toolbar';
import withTwoColumns from './plugins/withTwoColumns';
import { withTable } from './plugins/withTable';
import withList from './plugins/withList';
import { toHtml } from './utils/toHtml';
import { createParagraph } from './utils/paragraph';
import { withHtml } from './plugins/withHtml';
import './Editor.scss';

interface SlateEditorProps {
	initialValue?: EditorValue;
	onChange?: (value: EditorValue) => void;
	onBlur?: (result: { html: string; value: EditorValue }) => void;
	uploadRequestFunction?: ((data: FormData) => Promise<string>) | undefined;
}

export const SlateEditor = ({ initialValue, onChange, onBlur, uploadRequestFunction }: SlateEditorProps) => {
	const editorContainerRef = useRef<HTMLDivElement>(null);
	const editor: CustomEditor = useMemo(
		() =>
			withHistory(
				withHtml(withList(withTable(withTwoColumns(withEmbeds(withLinks(withReact(createEditor())))))))
			),
		[]
	);

	useEffect(() => {
		if (editorContainerRef.current) {
			editor.container = editorContainerRef.current;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorContainerRef]);

	const finalInitialValue = useMemo(() => {
		if (initialValue && Array.isArray(initialValue) && initialValue.length >= 1) {
			return initialValue;
		}
		return undefined;
	}, [initialValue]);
	const [value, setValue] = useState<EditorValue>(finalInitialValue || [createParagraph('')]);

	const handleEditorChange = (newValue: EditorValue) => {
		onChange?.(newValue);
		setValue(newValue);
	};

	const renderElement = useCallback((props: SlateRenderElementProps) => getBlock(props as RenderElementProps), []);

	const renderLeaf = useCallback(({ attributes, children, leaf }: RenderLeafProps) => {
		// eslint-disable-next-line no-param-reassign
		children = getMarked(leaf, children);
		return <span {...attributes}>{children}</span>;
	}, []);

	const [focused, setFocused] = useState(false);
	const handleFocus = () => {
		setFocused(true);
	};
	const handleBlur = () => {
		const html = toHtml(value as any);
		onBlur?.({
			html,
			value,
		});
		setFocused(false);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback((event) => {
		if (isHotkey('mod+u', event)) {
			event.preventDefault();
			toggleMark(editor, MarkFormat.UNDERLINE);
			return true;
		}
		if (isHotkey('mod+b', event)) {
			event.preventDefault();
			toggleMark(editor, MarkFormat.BOLD);
			return true;
		}
		if (isHotkey('mod+i', event)) {
			event.preventDefault();
			toggleMark(editor, MarkFormat.ITALIC);
			return true;
		}

		if (isHotkey(['mod+z', 'mod+y'], event)) {
			event.preventDefault();
			return true;
		}

		if (isHotkey(['esc'], event)) {
			ReactEditor.blur(editor);

			return true;
		}

		if (isHotkey('shift+enter', event)) {
			event.preventDefault();
			editor.insertText('\n');
			return true;
		}

		return true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div
			ref={editorContainerRef}
			className={classNames(
				'slate-editor-wrapper',
				getInputBaseClassName({
					focus: focused,
				}),
				'prevent-focus'
			)}
		>
			<Slate editor={editor} initialValue={value} onChange={handleEditorChange}>
				<Toolbar uploadRequestFunction={uploadRequestFunction} />
				<Editable
					placeholder="Napisz coś..."
					renderElement={renderElement}
					renderLeaf={renderLeaf}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
				/>
			</Slate>
		</div>
	);
};
